import type { Item } from '@ads/components-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { getApiV1NotificationMobileAppSharedClassStudents } from '@/shared/api/generated/clients/notificationMobileAppController/getApiV1NotificationMobileAppSharedClassStudents';
import type { ClassStudentNotificationViewModel } from '@/shared/api/generated/types/ClassStudentNotificationViewModel';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

const DEBOUNCE_MS = 2000;
const MIN_CHARS = 3;
const PAGE_SIZE = 20;

/**
 * Busca turmas com debounce de 2 s e mínimo de 3 caracteres.
 * Limpa os resultados imediatamente ao digitar; a busca real dispara após o debounce.
 * Páginas seguintes são buscadas em background enquanto `isOpen` for `true`.
 *
 * @param filter - Texto digitado pelo usuário
 * @param isOpen - Controla se o auto-fetch de páginas seguintes está ativo (ex: dropdown aberto)
 * @returns Lista acumulada de turmas e estado de sucesso da query
 *
 * @example
 * ```tsx
 * const { items, isQuerySuccess } = useNotificationClassroomSearch(classroomSearch, open);
 * ```
 */
export function useNotificationClassroomSearch(filter: string, isOpen: boolean) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter]);

  const isPending = filter !== debouncedFilter;
  const isReady = !isPending && debouncedFilter.length >= MIN_CHARS;

  const query = useInfiniteQuery({
    queryKey: ['notification-classroom-search', debouncedFilter],
    queryFn: ({ pageParam }) =>
      getApiV1NotificationMobileAppSharedClassStudents({
        searchText: debouncedFilter,
        pageNumber: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      unwrapPagedResponse<ClassStudentNotificationViewModel>(lastPage).length === PAGE_SIZE
        ? allPages.length + 1
        : undefined,
    enabled: isReady,
  });

  // Auto-fetch páginas seguintes enquanto o dropdown estiver aberto
  useEffect(() => {
    if (isOpen && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [isOpen, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const items: Item<string>[] = useMemo(
    () =>
      isPending
        ? []
        : (query.data?.pages ?? [])
            .flatMap(page => unwrapPagedResponse<ClassStudentNotificationViewModel>(page))
            .map(item => ({
              label: item.name ?? '',
              value: item.id?.toString() ?? '',
            })),
    [isPending, query.data],
  );

  return { items, isQuerySuccess: !isPending && query.isSuccess } as const;
}
